import { isApplicationAllowed, isPermissionAllowed, isUserDeactivated, isSelfRegistrationRequired } from '@infuse/ui/utils';
import { NavigationGuard } from 'vue-router';
import { Authenticator } from './authenticator';

/**
 * Navigation Guard for Authentication and Permissions
 *
 * - Redirects to login if route requires authentication (meta.authenticated) and user is not authenticated.
 * - Redirects authenticated users away from auth pages.
 * - Checks route permissions using meta.permissions and meta.check ('any' or 'all').
 * - If user lacks permission, redirects to 'not-allowed' page.
 * - Set meta.disablePermissionRouteGuard = true to skip permission checks for a route.
 * - Optionally checks for self-registration requirement if enabled.
 */

export interface NavigationGuardOptions {
  enableSelfRegistrationCheck?: boolean;
}

export const createNavigationGuard = (
  authenticator: Authenticator,
  options?: NavigationGuardOptions
): NavigationGuard => {
  const { enableSelfRegistrationCheck = false } = options || {};

  return async (to, _, next) => {
    try {
      const isAuthenticated = await authenticator.isAuthenticated();

      if (!isAuthenticated && to.meta['authenticated']) {
        return next({ name: 'auth-login' });
      }

      if (to.path.includes('auth') && isAuthenticated) {
        return next({ path: '/' });
      }

      if (isUserDeactivated() && to.name !== 'user-deactivated' && !to.path.includes('auth')) {
        return next({ name: 'user-deactivated' });
      }

      // Check for self-registration requirement if enabled
      if (enableSelfRegistrationCheck && isAuthenticated) {
        if (isSelfRegistrationRequired() && to.name !== 'self-registration' && !to.path.includes('auth')) {
          return next({ name: 'self-registration' });
        }
      }

      if (
        !isUserDeactivated() &&
        !isApplicationAllowed() &&
        to.name !== 'application-not-allowed' &&
        !to.path.includes('auth')
      ) {
        return next({ name: 'application-not-allowed' });
      }

      const disablePermissionRouteGuard = to.meta?.['disablePermissionRouteGuard'] || false;

      if (!disablePermissionRouteGuard) {
        if (!isPermissionAllowed(to.meta)) {
          return next({ name: 'not-allowed' });
        }
      }

      return next();
    } catch (err) {
      // in case of uncaught errors, also return 'not-allowed' - to prevent users with broken login sessions from ending up on frozen blank screens
      console.error(err);
      return next({ name: 'application-not-allowed' });
    }
  };
};
